import { useState, useCallback } from 'react';
import LogicFlow from '@logicflow/core';
import {
  DataMapApi,
  DataMapApiResponse,
  mapViewsAddTableJoinsParmas,
} from '@/api/dataMapApi';
import { message } from 'antd';

interface UseRelationshipModalProps {
  mapViewsId: string | null;
  lfRef: React.RefObject<LogicFlow | null>;
}

export interface RelationshipSubmitDataCondition {
  id: string;
  leftField: string;
  leftFieldId?: string;
  rightField: string;
  rightFieldId?: string;
  conditionType: string;
}

export interface RelationshipDataConditions {
  id: string;
  leftColumnId: string;
  leftColumnName: string;
  rightColumnId: string;
  rightColumnName: string;
  // leftField: string;
  // leftFieldId: string;
  // rightField: string;
  // rightFieldId: string;
  operator: string;
}

export interface RelationshipDataType {
  leftTable: { tableName: string; tableId: string; fields: any[] };
  rightTable: { tableName: string; tableId: string; fields: any[] };
  sourceAnchorId?: string;
  targetAnchorId?: string;
  conditions: RelationshipDataConditions[];
}

export interface RelationshipSubmitData {
  leftTable: string;
  leftTableId?: string;
  rightTable: string;
  rightTableId?: string;
  relationshipType: string;
  conditions: RelationshipDataConditions[];
}

const useRelationshipModal = ({
  lfRef,
  mapViewsId,
}: UseRelationshipModalProps) => {
  // 关联弹窗状态
  const [relationshipModalVisible, setRelationshipModalVisible] =
    useState(false);
  // 关联弹窗的表数据
  const [relationshipData, setRelationshipData] =
    useState<RelationshipDataType>({
      leftTable: { tableName: '', tableId: '', fields: [] },
      rightTable: { tableName: '', tableId: '', fields: [] },
      sourceAnchorId: '',
      targetAnchorId: '',
      conditions: [],
    });
  // 当前正在处理的连线ID
  const [currentEdgeId, setCurrentEdgeId] = useState<string | null>(null);
  // 存储弹窗选中的关系数据
  const [selectedRelationship, setSelectedRelationship] =
    useState<RelationshipSubmitData | null>(null);

  // 处理关联弹窗确认
  const handleRelationshipConfirm = useCallback(
    async (relationship: RelationshipSubmitData) => {
      if (!lfRef.current) {
        setRelationshipModalVisible(false);
        return;
      }
      console.log('处理关联弹窗确认', currentEdgeId, relationship);

      // 保存选中的关系数据
      setSelectedRelationship(relationship);

      // 删除原有的手动连线
      if (currentEdgeId) {
        try {
          console.log('删除原有连线失败', currentEdgeId);
          lfRef.current.deleteEdge(currentEdgeId);
        } catch (error) {
          console.error('删除原有连线失败:', error);
        }
        setCurrentEdgeId(null);
      }

      // 获取所有节点和连线
      const allNodes = lfRef.current.graphModel.nodes;
      const allEdges = lfRef.current.graphModel.edges;

      // 直接使用relationship对象中的表名查找节点
      const leftNode = allNodes.find(
        node => String(node.id) === String(relationship.leftTableId)
      );
      const rightNode = allNodes.find(
        node => String(node.id) === String(relationship.rightTableId)
      );
      // 检查当前锚点是否已经和其他表锚点发生关系
      let existingEdgeId = null;
      if (
        leftNode &&
        rightNode &&
        relationshipData.sourceAnchorId &&
        relationshipData.targetAnchorId
      ) {
        console.log('查找existingEdgeId:', {
          leftNodeId: leftNode.id,
          rightNodeId: rightNode.id,
          currentEdgeId: currentEdgeId,
          allEdgesCount: allEdges.length
        });
        
        // 查找连接相同两个表的现有边，排除当前正在处理的临时边
        allEdges.forEach(edge => {
          // 排除当前正在处理的临时边
          if (edge.id === currentEdgeId) {
            console.log('跳过临时边:', edge.id);
            return;
          }
          
          const connectsSameTables =
            (String(edge.sourceNodeId) === String(leftNode.id) &&
              String(edge.targetNodeId) === String(rightNode.id)) ||
            (String(edge.sourceNodeId) === String(rightNode.id) &&
              String(edge.targetNodeId) === String(leftNode.id));
          
          console.log('检查边:', {
            edgeId: edge.id,
            sourceNodeId: edge.sourceNodeId,
            targetNodeId: edge.targetNodeId,
            connectsSameTables: connectsSameTables
          });
          
          if (connectsSameTables) {
            existingEdgeId = edge.id;
            console.log('找到existingEdgeId:', existingEdgeId);
          }
        });
      }
      let edgeId = '';
      // 调用添加 || 更新边API
      if (mapViewsId) {
        try {
          const globalConfig = (window as any)?._intellix_global_config ?? {}

          let params: mapViewsAddTableJoinsParmas = {
            leftTableId: Number(relationship.leftTableId) || null,
            rightTableId: Number(relationship.rightTableId) || null,
            joinType: relationship.relationshipType.toLocaleUpperCase(),
            conditions: relationship.conditions.map(e => ({
              leftColumnId: Number(e.leftColumnId) || null,
              rightColumnId: Number(e.rightColumnId) || null,
              operator: globalConfig.operatorEnum[e.operator] || e.operator || '',
            })),
          };
          let res: DataMapApiResponse<string>;
          console.log(
            'existingEdgeId',
            existingEdgeId,
            allEdges,
            params,
            relationship
          );
          console.log('准备调用API:', {
            existingEdgeId: existingEdgeId,
            params: params
          });
          
          if (existingEdgeId) {
            // 第二次更新时，使用existingEdgeId作为join ID
            console.log('调用update接口:', existingEdgeId);
            res = await DataMapApi.mapViewsUpdateTableJoins(existingEdgeId, params);
            // 更新成功后，不删除旧边线，而是更新其属性
            // 删除旧边线会触发 edge:delete 事件，导致调用删除API
          } else {
            // 第一次添加时，使用mapViewsId作为映射视图ID
            console.log('调用add接口:', mapViewsId);
            res = await DataMapApi.mapViewsAddTableJoins(mapViewsId, params);
          }
          console.log('API调用成功:', res);
          if (!!res?.code && res?.code === 200) {
            edgeId = res?.data || existingEdgeId as unknown as string || '';
            console.log('获取到edgeId:', edgeId);
            message.success(existingEdgeId ? '更新边信息成功' : '添加边信息成功');
          }
        } catch (error) {
          console.error('API调用失败:', error);
          message.error('添加边信息失败，请检查添加边');
          return;
        }
      }

      // 注意：不在这里删除任何旧连线
      // 因为删除操作会触发 edge:delete 事件，导致调用删除API
      // 如果是新建连线，直接创建即可
      // 如果是更新连线，通过 setProperties 更新边线属性即可

      if (leftNode && rightNode) {
        const sourceNode = leftNode;
        const targetNode = rightNode;

        const edgeData = {
          id: edgeId,
          sourceNodeId: sourceNode.id,
          targetNodeId: targetNode.id,
          sourceAnchorId: `${sourceNode.id}_table_right`,
          targetAnchorId: `${targetNode.id}_table_left`,
          type: 'sql-edge-bezier',
          properties: {
            relationshipType: relationship.relationshipType,
            leftTableId: relationship.leftTableId,
            rightTableId: relationship.rightTableId,
            conditions: relationship.conditions,
            stroke: '#333',
            strokeWidth: 2,
            autoCreated: true,
            isBackendEdge: true, // 标记为后端API创建的边线（已有后端ID）
          },
        };

        // 如果是更新现有边线，则更新其属性；否则添加新边线
        try {
          if (existingEdgeId) {
            console.log('更新现有边线属性:', existingEdgeId);
            const edgeModel = lfRef.current.getEdgeModelById(existingEdgeId);
            if (edgeModel) {
              edgeModel.setProperties(edgeData.properties);
              console.log('成功更新边线属性:', existingEdgeId);
            }
          } else {
            console.log('准备添加新边线:', edgeData);
            // 确保 edgeId 有效后再添加边线
            if (edgeId) {
              lfRef.current.addEdge(edgeData);
              console.log('成功添加边线:', edgeId);
            } else {
              console.error('无法添加边线：edgeId 为空');
              message.error('创建边线失败，请重试');
            }
          }
        } catch (error) {
          console.error('添加/更新边线失败:', error);
        }
      } else {
        console.error('未找到表节点:', {
          leftTable: relationship.leftTable,
          rightTable: relationship.rightTable,
          allTableNames: allNodes.map(node => node.properties.tableName),
        });
      }

      // 关闭关联弹窗
      setRelationshipModalVisible(false);
    },
    [lfRef, currentEdgeId, mapViewsId, relationshipData]
  );

  // 处理关联弹窗取消
  const handleRelationshipCancel = useCallback(() => {
    setRelationshipModalVisible(false);
    setCurrentEdgeId(null);
  }, []);

  // 打开关联弹窗
  const openRelationshipModal = useCallback(
    (data: RelationshipDataType, edgeId: string | null) => {
      console.log('openRelationshipModal', data);

      setRelationshipData(data);
      setCurrentEdgeId(edgeId);
      setRelationshipModalVisible(true);
    },
    []
  );

  return {
    relationshipModalVisible,
    relationshipData,
    currentEdgeId,
    selectedRelationship,
    handleRelationshipConfirm,
    handleRelationshipCancel,
    openRelationshipModal,
    setRelationshipModalVisible,
    setRelationshipData,
    setCurrentEdgeId,
    setSelectedRelationship,
  };
};

export default useRelationshipModal;
